import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon, StarIcon, CheckBadgeIcon, ArrowDownTrayIcon, PaperClipIcon, ShoppingCartIcon } from '@heroicons/react/24/outline';
import { inventoryAPI } from '../../../services/inventoryApi';
import { openFileInNewTab } from '../../../services/fileApi';
import { useAuth } from '../../../contexts/AuthContext';
import { downloadXlsxFromAoa } from '../../../utils/excelJsSheetHelpers';

const money = (n) => `Rs ${(Math.round((Number(n) || 0) * 100) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

/**
 * Analyse a purchase request's supplier quotations side by side (PR lines × suppliers), select the
 * winning quotation (a reason is required when it is not the lowest total) and raise the PO from it.
 */
const QuotationComparison = ({ isOpen, onClose, prId, onChanged }) => {
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [selectTarget, setSelectTarget] = useState(null); // quotation being selected (reason dialog)
    const [reason, setReason] = useState('');

    const canSelect = hasPermission(['INVENTORY:QUOTATIONS:SELECT']);
    const canCreatePO = hasPermission(['INVENTORY:PURCHASE_ORDERS:CREATE']);

    const load = useCallback(async () => {
        if (!prId) return;
        setLoading(true);
        try {
            const res = await inventoryAPI.compareSupplierQuotations(prId);
            setData(res.data);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to load the comparison');
        } finally { setLoading(false); }
    }, [prId]);

    useEffect(() => { if (isOpen) load(); }, [isOpen, load]);

    if (!isOpen) return null;

    const pr = data?.purchaseRequest;
    const quotations = data?.quotations || [];
    const lines = data?.lines || [];
    const open = pr?.status === 'approved';
    const selected = quotations.find(q => q.status === 'selected');

    const doSelect = async () => {
        const q = selectTarget;
        if (!q.isLowestTotal && !reason.trim()) { toast.error('Enter the reason for selecting a quotation that is not the lowest'); return; }
        setBusy(true);
        try {
            const res = await inventoryAPI.selectSupplierQuotation(q._id, reason.trim());
            toast.success(res.message || 'Quotation selected');
            setSelectTarget(null); setReason('');
            await load(); onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to select the quotation');
        } finally { setBusy(false); }
    };

    const doUnselect = async (q) => {
        setBusy(true);
        try {
            const res = await inventoryAPI.unselectSupplierQuotation(q._id);
            toast.success(res.message || 'Selection removed');
            await load(); onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to remove the selection');
        } finally { setBusy(false); }
    };

    // Raise the purchase order: the PO form opens with this request, its selected quotation's supplier and prices
    const createPO = () => {
        onClose?.();
        navigate('/inventory/purchase-orders', { state: { selectedPRId: pr._id, fromPR: true, from: '/inventory/supplier-quotations' } });
    };

    const exportExcel = async () => {
        const head = ['Line', 'Description', 'Qty', 'Estimate / unit', ...quotations.flatMap(q => [`${q.supplier?.name} (${q.quotationNumber}) unit`, 'line total'])];
        const rows = lines.map(l => [l.lineNo, l.description, `${l.quantity} ${l.uom || ''}`, l.estimatedUnitPrice,
            ...l.cells.flatMap(c => (c.missing ? ['—', '—'] : [c.unitPrice, c.lineTotal]))]);
        const foot = (label, f) => [label, '', '', '', ...quotations.flatMap(q => [f(q), ''])];
        await downloadXlsxFromAoa([
            [`Quotation comparison — ${pr.prNumber}`], [], head, ...rows, [],
            foot('Subtotal', q => q.totals.subtotal), foot('Discount', q => q.totals.discount), foot('Tax', q => q.totals.tax),
            foot('Total', q => q.totals.grandTotal), foot('Lowest total', q => (q.isLowestTotal ? 'YES' : '')),
            foot('Delivery (days)', q => q.deliveryDays ?? ''), foot('Payment terms', q => q.paymentTerms || ''),
            foot('Valid until', q => fmtDate(q.validUntil)), foot('Status', q => q.status.replace('_', ' ')),
            foot('Selection reason', q => q.selectionReason || ''),
        ], 'Comparison', `Quotation-comparison-${pr.prNumber}.xlsx`, [{ wch: 6 }, { wch: 36 }, { wch: 10 }, { wch: 14 }]);
    };

    const th = 'px-3 py-2 text-xs font-medium text-gray-500 uppercase';
    const footRow = (label, render, strong = false) => (
        <tr className={strong ? 'bg-gray-50' : ''}>
            <td colSpan={3} className={`px-3 py-1.5 text-xs ${strong ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>{label}</td>
            {quotations.map(q => <td key={q._id} className={`px-3 py-1.5 text-xs text-right ${q.status === 'selected' ? 'bg-green-50' : ''}`}>{render(q)}</td>)}
        </tr>
    );

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="quotation-comparison">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[94vh] flex flex-col">
                <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-lg">
                    <div>
                        <h3 className="text-base font-semibold text-white">Analyse Quotations {pr ? `— ${pr.prNumber}` : ''}</h3>
                        {pr && <p className="text-xs text-indigo-100">{[pr.costCenterLabel, (pr.procurementType === 'FIXED_ASSET' ? 'Fixed asset' : pr.procurementType === 'SERVICE' ? 'Service' : 'Inventory'), `${quotations.length} quotation${quotations.length === 1 ? '' : 's'}`, String(pr.status).replace(/_/g, ' ')].filter(Boolean).join(' · ')}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                        {quotations.length > 0 && (
                            <button onClick={exportExcel} className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-white bg-white/20 rounded hover:bg-white/30">
                                <ArrowDownTrayIcon className="w-4 h-4 mr-1" /> Excel
                            </button>
                        )}
                        <button onClick={onClose} aria-label="Close" className="text-white/80 hover:text-white"><XMarkIcon className="w-5 h-5" /></button>
                    </div>
                </div>

                <div className="overflow-auto flex-1 p-4">
                    {loading ? (
                        <p className="text-sm text-gray-400 p-6 text-center">Loading comparison…</p>
                    ) : quotations.length === 0 ? (
                        <p className="text-sm text-gray-500 p-6 text-center">No quotations entered for this purchase request yet.</p>
                    ) : (
                        <>
                            {selected && (
                                <div className="mb-3 px-3 py-2 rounded border border-green-200 bg-green-50 text-sm text-green-800 flex items-center justify-between gap-3">
                                    <span>
                                        <CheckBadgeIcon className="w-4 h-4 inline mr-1" />
                                        Selected: <b>{selected.supplier?.name}</b> ({selected.quotationNumber}) — {money(selected.totals.grandTotal)}
                                        {selected.selectionReason && <span className="block text-xs text-green-700 mt-0.5">Reason: {selected.selectionReason}</span>}
                                    </span>
                                    {open && canCreatePO && (
                                        <button onClick={createPO} className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 whitespace-nowrap">
                                            <ShoppingCartIcon className="w-4 h-4 mr-1" /> Create Purchase Order
                                        </button>
                                    )}
                                    {!open && <span className="text-xs text-green-700">Purchase order raised</span>}
                                </div>
                            )}
                            <table className="min-w-full text-sm border border-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className={`${th} text-left`}>#</th>
                                        <th className={`${th} text-left`}>Description</th>
                                        <th className={`${th} text-right`}>Qty / Estimate</th>
                                        {quotations.map(q => (
                                            <th key={q._id} className={`px-3 py-2 text-xs font-medium text-gray-500 text-right ${q.status === 'selected' ? 'bg-green-100' : ''}`}>
                                                <div className="text-sm font-semibold text-gray-900">{q.supplier?.name}</div>
                                                <div className="text-[11px] text-gray-500 font-normal">{q.quotationNumber}{q.supplierReference ? ` · ${q.supplierReference}` : ''}</div>
                                                {q.status === 'selected' && <span className="inline-flex items-center text-[10px] text-green-800"><CheckBadgeIcon className="w-3 h-3 mr-0.5" />Selected</span>}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {lines.map(l => (
                                        <tr key={String(l.prLineId)}>
                                            <td className="px-3 py-2 text-gray-700">{l.lineNo}</td>
                                            <td className="px-3 py-2 text-gray-900">{l.description}</td>
                                            <td className="px-3 py-2 text-right whitespace-nowrap">
                                                {l.quantity} {l.uom}
                                                <div className="text-[10px] text-gray-400">est. {money(l.estimatedUnitPrice)}</div>
                                            </td>
                                            {l.cells.map(c => (
                                                <td key={String(c.quotationId)} data-lowest={c.isLowest ? 'yes' : 'no'}
                                                    className={`px-3 py-2 text-right whitespace-nowrap ${c.isLowest ? 'bg-emerald-50' : ''}`}>
                                                    {c.missing ? <span className="text-gray-400">—</span> : (
                                                        <>
                                                            <div className={`font-medium ${c.isLowest ? 'text-emerald-700' : 'text-gray-900'}`}>{money(c.lineTotal)}</div>
                                                            <div className="text-[10px] text-gray-500">
                                                                {money(c.unitPrice)} / unit{c.discountPercent ? ` · −${c.discountPercent}%` : ''}{c.taxPercent ? ` · +${c.taxPercent}% tax` : ''}
                                                            </div>
                                                        </>
                                                    )}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="border-t-2 border-gray-200">
                                    {footRow('Subtotal', q => money(q.totals.subtotal))}
                                    {footRow('Discount', q => `− ${money(q.totals.discount)}`)}
                                    {footRow('Tax', q => money(q.totals.tax))}
                                    {footRow('Total', q => (
                                        <span data-lowest-total={q.isLowestTotal ? 'yes' : 'no'} className={`text-sm font-bold ${q.isLowestTotal ? 'text-emerald-700' : 'text-gray-900'}`}>
                                            {money(q.totals.grandTotal)}
                                            {q.isLowestTotal && quotations.length > 1 && <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-amber-100 text-amber-800"><StarIcon className="w-3 h-3 mr-0.5" />Lowest</span>}
                                        </span>
                                    ), true)}
                                    {footRow('Delivery', q => (q.deliveryDays != null ? `${q.deliveryDays} days` : '—'))}
                                    {footRow('Payment terms', q => q.paymentTerms || '—')}
                                    {footRow('Valid until', q => fmtDate(q.validUntil))}
                                    {footRow('Documents', q => (q.attachments?.length ? q.attachments.map((a, i) => (
                                        <button key={a.fileId || i} type="button" onClick={() => openFileInNewTab(a.fileId).catch(() => toast.error('Could not open the document'))}
                                            className="inline-flex items-center text-indigo-600 hover:underline"><PaperClipIcon className="w-3 h-3 mr-0.5" />{a.name}</button>
                                    )) : '—'))}
                                    {open && canSelect && footRow('', q => (q.status === 'selected' ? (
                                        <button disabled={busy} onClick={() => doUnselect(q)} className="px-2.5 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">Unselect</button>
                                    ) : (
                                        <button disabled={busy} onClick={() => { setSelectTarget(q); setReason(''); }}
                                            className="px-2.5 py-1 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 disabled:opacity-50" aria-label={`Select ${q.supplier?.name}`}>
                                            Select
                                        </button>
                                    )))}
                                </tfoot>
                            </table>
                            <p className="mt-2 text-[11px] text-gray-500">Green cells are the lowest price for that line; ★ marks the lowest total. Selecting a quotation that is not the lowest needs a reason.</p>
                        </>
                    )}
                </div>
            </div>

            {/* Select — reason (required when not the lowest total) */}
            {selectTarget && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                        <div className="px-4 py-3 border-b border-gray-200">
                            <h4 className="text-sm font-semibold text-gray-900">Select {selectTarget.supplier?.name} ({selectTarget.quotationNumber})</h4>
                            <p className="text-xs text-gray-500 mt-0.5">Quoted total {money(selectTarget.totals.grandTotal)}</p>
                        </div>
                        <div className="p-4 space-y-2">
                            {!selectTarget.isLowestTotal && (
                                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded p-2">
                                    This is not the lowest quotation — lowest is {money(Math.min(...quotations.map(q => q.totals.grandTotal)))}. A reason is required.
                                </p>
                            )}
                            <label className="block text-xs font-medium text-gray-600">
                                Reason for selection {!selectTarget.isLowestTotal && <span className="text-red-500">*</span>}
                            </label>
                            <textarea rows={3} value={reason} onChange={e => setReason(e.target.value)} aria-label="Selection reason"
                                placeholder={selectTarget.isLowestTotal ? 'Optional — e.g. lowest price' : 'e.g. faster delivery, better warranty, quality…'}
                                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 resize-none" />
                        </div>
                        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                            <button onClick={() => setSelectTarget(null)} className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
                            <button disabled={busy} onClick={doSelect} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50">Confirm Selection</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuotationComparison;
